import { createFileRoute } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";
import printedTent from "@/assets/bluebonnet/printed-logo-vendor-tent.jpg";
import tentCurtains from "@/assets/bluebonnet/tent-curtains-drapes.jpg";
import tentLighting from "@/assets/bluebonnet/tent-string-lighting.jpg";
import festivalRow from "@/assets/bluebonnet/festival-tent-row-sidewalls.jpg";
import weddingTentHero from "@/assets/bluebonnet/wedding-tent-hero.jpg";
import letsTalkTent from "@/assets/bluebonnet/lets-talk-event-tent.jpg";
import funbrella from "@/assets/bluebonnet/funbrella-pool-shade.jpg";
import glampingRepairs from "@/assets/bluebonnet/glamping-tent-repairs.jpg.asset.json";

const collection = getCollection("bluebonnet-series")!;

const content: FlagshipContent = {
  formImageSrc: letsTalkTent,
  formImageAlt: "White peak-frame event tent set up on a Texas ranch lawn at golden hour",
  eyebrow: "Signature Collection · Tents · Safari & Glamping · Vendor Booths · Outdoor Markets",
  heroImageSrc: weddingTentHero,
  showRangerCare: false,
  serviceImageSrc: funbrella,
  serviceImageAlt: "Blue and yellow funbrella umbrella shade structure over a Texas pool deck and splash pad",
  heroImageAlt: "White wedding reception tent with draped fabric and string lights on a Texas lawn at sunset",
  heroKicker: "Tents · Safari & Glamping Tents · Vendor Booths · Boutiques · Outdoor Markets · San Antonio · Austin · Seguin",
  lifestyleIntro:
    "The Bluebonnet Series is our Tents, Vendor Booths & Outdoor Markets division — commercial pop-up canopies, frame tents, printed tops and sidewalls, boutique setups, safari and glamping tents, table covers and banners that take your brand on the road.",
  lifestyleBody:
    "This is our Outdoor Portable division: commercial pop-up canopies, frame tents, printed tent tops, sidewalls, table covers, and banners. We purchase commercial-grade tent systems and hardware, then customize them in our Seguin shop with in-house sewing, printing partners, and repair service. Need a hand on site? For an additional cost we can help set them up — so your booth looks the same on year three as it did on day one. We also sell, build and repair custom safari and glamping tents — canvas wall tents, safari-style tents and glamping suites for ranch stays, wedding venues and short-term rental properties, with re-sewn seams, new zippers, replacement windows and full canvas recovers done in our own shop.",
  solutionsHeading: "Tents, booths & market gear",
  solutions: [
    { title: "Tent Curtains & Drapes", desc: "Sewn-to-fit curtain panels and drapes for event and boutique tents.", to: "/estimate-quotes#general" },
    { title: "Tent Lighting", desc: "String and bistro lighting run through the frame for evening markets.", to: "/outdoor-lighting" },
    { title: "Commercial Event Tents", desc: "Frame, peak and pop-up tents for events, festivals and weekly market use.", to: "/commercial-event-tents" },
    { title: "Custom Printed Tops", desc: "Your logo, colors, and booth number printed on the canopy top.", to: "/commercial-event-tents#custom-printed-tent-tops" },
    { title: "Sidewalls & Half-Walls", desc: "Solid, window, and mesh walls for sun, wind, and overnight security.", to: "/commercial-event-tents#tent-sidewalls" },
    { title: "Vendor & Farmers-Market Tents", desc: "Complete booth packages for vendors, farmers markets and festivals.", to: "/commercial-event-tents#vendor-and-farmers-market-tents" },
    { title: "Table Covers & Banners", desc: "Printed table throws, feather flags, and hanging banners sewn in-house.", to: "/covers-tops" },
    { title: "Tent Repair & Recover", desc: "Torn tops re-sewn, zippers replaced, frames and parts sourced.", to: "/estimate-quotes#general" },
    { title: "Glamping & Safari Tents (Rio Grande Series™)", desc: "Luxury canvas lodging for hotels, resorts, glamping properties and safari camps.", to: "/safari-glamping-tents" },
    { title: "Glamping Tent Repairs", desc: "Seams re-sewn, zippers and windows replaced, canvas patched or fully recovered.", to: "/safari-glamping-tents" },
    { title: "Custom Fabrication", desc: "Weights, brackets, and welded add-ons for booth setups.", to: "/custom-fabrication" },
    { title: "Fabrics & Colors", desc: "See the canvas, mesh, and vinyl options we build with.", to: "/fabrics" },
  ],
  trustBadges: [
    "Commercial-grade tent systems",
    "Commercial-grade frames & tops",
    "In-house sewing & repair in Seguin",
    "Vendor, festival & market ready",
  ],
  galleryHeading: "Printed tops, curtains, sidewalls, lighting & glamping tent repairs",
  gallery: [
    {
      src: printedTent,
      alt: "Commercial pop-up vendor tent with a custom printed logo canopy top and valance at a Texas farmers market",
      caption: "Custom printed tent tops — your logo, colors, and tagline printed on the canopy and valance, with matching table throws and banners sewn in Seguin.",
    },
    {
      src: tentCurtains,
      alt: "White frame tent with flowing fabric curtains and tie-backs at an outdoor boutique market",
      caption: "Tent curtains & drapes — sewn-to-fit curtain panels with tie-backs for boutique pop-ups, hospitality booths, and event tents.",
    },
    {
      src: tentLighting,
      alt: "Large frame tent at dusk lit with warm string lighting over a festival beer garden",
      caption: "Tent lighting — string and bistro lighting run through the frame so your booth or beer garden keeps selling after sundown.",
    },
    {
      src: glampingRepairs.url,
      alt: "Interior of a canvas safari glamping tent showing the sewn roof seam, ridge, and a rolled canvas window flap with tie chains during a glamping tent repair",
      caption: "Glamping tent repairs — canvas seams re-sewn, ridge and roof panels patched, window flaps, tie chains and zippers rebuilt in our Seguin shop.",
    },
    {
      src: festivalRow,
      alt: "Row of commercial canopy tents with mesh and clear window sidewalls, printed backdrops, and feather flags at a craft festival",
      caption: "Sidewalls, half-walls & flags — solid, mesh, and clear-window walls for sun, wind, and overnight security, plus feather flags and printed backdrops.",
    },
  ],
  faqs: [
    {
      q: "What kind of tents do you sell?",
      a: "Commercial-grade pop-up canopies and frame tents — not big-box weekend tents. We purchase commercial-grade tent hardware and tops, then customize, print, and service them here in Texas — and for an additional cost we can help set them up on site.",
    },
    {
      q: "Can you print my business logo on the tent?",
      a: "Yes. Custom printed tops, valances, sidewalls, table throws, and feather flags — matched to your brand colors so your booth reads from across the market.",
    },
    {
      q: "Can you light my tent or booth?",
      a: "Yes — string and bistro lighting run through the tent frame. For permanent backyard and patio lighting, that lives in our Hill Country Series™ Outdoor Lighting page, powered by Bosso Smart Lighting, with smart controls through our Lone Star Series™ Smart Lighting & Automation.",
    },
    {
      q: "Who is the Bluebonnet Series for?",
      a: "Vendors and boutiques at farmers markets and craft fairs, food trucks and concession operators, festival and event organizers, mobile boutiques, and any business that sets up outdoors on weekends.",
    },
    {
      q: "Do you build and repair safari and glamping tents?",
      a: "Yes — we sell safari and glamping tents, build custom canvas tents to your dimensions, and repair the ones you already own. Common work includes re-sewing roof and wall seams, replacing zippers and window flaps, patching storm damage and fully recovering the canvas. See our Safari & Glamping Tents page for details.",
    },
    {
      q: "Do you repair tents you didn't sell?",
      a: "Yes — torn tops, blown-out seams, broken zippers, and bent frames. Bring it to our Seguin shop and we'll re-sew it or source replacement parts.",
    },
    {
      q: "Where do you deliver?",
      a: "Austin, San Antonio, New Braunfels, Seguin, Gruene, and markets throughout Central and South Texas.",
    },
  ],
};

export const Route = createFileRoute("/bluebonnet-series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Bluebonnet Series™ — Tents, Vendor Booths & Outdoor Markets | Texas Made Shade",
      description: collection.description,
      keywords:
        "safari tents texas, glamping tents texas, glamping tent repairs san antonio, custom canvas tents texas, vendor tents texas, tent rental alternative texas, pop up canopy san antonio, custom printed tent tops austin, farmers market tent, festival tents texas, boutique pop up tent, outdoor market canopy, tent repair texas",
    }),
    links: leafCanonicalLink(ctx, collection.path),
    scripts: serviceHeadScripts({
      path: collection.path,
      serviceName: "Bluebonnet Series Tents, Vendor Booths & Outdoor Markets",
      serviceType: "Commercial tents, vendor booths, printed canopies & outdoor market shade",
      description: collection.description,
      faqs: content.faqs,
      breadcrumbName: "Bluebonnet Series — Tents, Vendor Booths & Outdoor Markets",
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
